export interface TranscriptSentence {
  text: string;
  start: number;
  end: number;
  /** AI confidence (0–1) in the transcribed content. Absent on legacy transcripts. */
  confidence?: number;
  /** AI confidence (0–1) in the speaker label for this sentence. Absent on legacy transcripts. */
  speakerConfidence?: number;
  [key: string]: unknown;
}

export interface TranscriptParagraph {
  start: number;
  end: number;
  num_words?: number;
  speaker: string | number;
  sentences: TranscriptSentence[];
  /**
   * Transient client-only id assigned when a draft is initialized. Used as a
   * stable React key and to track per-entry edit state across reorder/insert.
   * Stripped before the document is sent to the server.
   */
  _uid?: string;
  [key: string]: unknown;
}

export interface TranscriptData {
  paragraphs: TranscriptParagraph[];
  [key: string]: unknown;
}

export interface TranscriptListItem {
  evidenceId: string;
  versionNumber: number;
  createdAt: number;
  recordedAt: number;
  fileName: string;
  wordCount: number;
  transcribedBy: string;
}

export interface TranscriptListResponse {
  transcripts: TranscriptListItem[];
  nextToken?: string;
}

export interface UpdateTranscriptResponse {
  versionNumber: number;
  createdAt: number;
  editedBy: string;
  wordCount: number;
}

/** The transcript document plus the version it was loaded at (for optimistic concurrency). */
export interface LoadedTranscript {
  transcript: TranscriptData;
  versionNumber: number;
}

/** One saved revision in a transcript's version history. */
export interface TranscriptVersionSummary {
  versionNumber: number;
  /** Epoch ms this revision was saved. */
  createdAt?: number;
  /** User id (or system marker) that saved this revision. */
  editedBy?: string;
  wordCount?: number;
}

export interface TranscriptVersionsResponse {
  versions: TranscriptVersionSummary[];
}

/**
 * Full-document save payload. The whole edited transcript replaces the stored
 * one; the server always appends a new version continuing from the current head
 * (no optimistic-concurrency / baseVersion check).
 */
export interface SaveTranscriptRequest {
  transcript: TranscriptData;
}

/** @deprecated Superseded by full-document {@link SaveTranscriptRequest}. Kept during migration. */
export type JsonPatchReplaceOp = {
  op: "replace";
  path: string;
  value: string;
};

/** @deprecated Superseded by full-document {@link SaveTranscriptRequest}. Kept during migration. */
export type JsonPatchOperation = JsonPatchReplaceOp;

export const LEGACY_TRANSCRIBED_BY = "system:legacy-migration";
